import { ReactNode, memo, useCallback } from 'react';

import { UserRole } from 'app-types';
import { FormattedMessage } from 'localization';
import { AdminSettingsDisclosure, DefaultSidebar } from 'ui/molecules';

import { useGetAdminMenuItems } from './AdminSettingsSidebar.hooks';

export type SettingsSidebarMenuItem = {
  data?: {
    value: string;
    label: JSX.Element;
    isNew?: boolean;
    roles?: UserRole[];
    icon?: ReactNode;
    // First-level settings-section key (see src/lib/roles.js). When set, this
    // item is hidden unless the user's access includes it. Used to split a
    // section (e.g. General Settings → users / rolesAndPermissions).
    sectionKey?: string;
  }[];
  link?: string;
  icon?: ReactNode;
  title?: string;
  translationKey?: string;
  hasDivider?: boolean;
  oldApp?: boolean;
  topLabel?: ReactNode;
  count?: ReactNode;
  // First-level settings-section key gating the whole section.
  sectionKey?: string;
};

function AdminSettingsSidebarBase({ navigate, pathname }: { navigate: any; pathname: string }) {
  const { menuItems, isLoading } = useGetAdminMenuItems();

  const onChangeTab = useCallback((newPathname: string) => navigate(newPathname), []);

  return (
    <DefaultSidebar className="flex flex-col relative">
      <div className="pb-4 flex-1">
        <div className="font-semibold text-lg px-8 sticky py-6 top-0 bg-v2blueGray-150/50 backdrop-blur-md z-10">
          <FormattedMessage id="desk-app.settings" />
        </div>

        {!isLoading && (
          <div className="px-4 flex flex-col gap-2">
            {menuItems.map(({ data, title, translationKey, icon, hasDivider, link }, index) => {
              const sectionName = translationKey ? (
                <FormattedMessage id={translationKey} />
              ) : (
                <>{title}</>
              );

              return (
                <AdminSettingsDisclosure
                  tabIndex={index}
                  key={`menu-item-${translationKey}-${index}`}
                  exact={false}
                  data={data}
                  sectionName={sectionName}
                  openTab={pathname}
                  onChangeTab={onChangeTab}
                  icon={icon}
                  className={hasDivider ? 'border-b border-v2blueGray-200 pb-4 mb-4' : ''}
                  link={link}
                />
              );
            })}
          </div>
        )}
      </div>
    </DefaultSidebar>
  );
}

export const AdminSettingsSidebar = memo(AdminSettingsSidebarBase);
